import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import CloseIcon from '@mui/icons-material/Close'
import { Link, useCustom, useApiUrl } from '@refinedev/core'
import React, { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@/utils/auth/auth0'
import axios from 'axios'
import UpcomingRenewals from './UpcomingRenewals'
import DateRangeFilter from './DateRangeFilter'
import { getTierLabel } from '../utils/tierUtils'

interface SelectedOrg {
  created_at: string
  has_regions: boolean
  latest_scan_date: string
  membership: string
  name: string
  profile_text: string
  tier: number
  uuid: string
}

interface StepStatus {
  completed: boolean
  active: boolean
  route: string
  label: string
}

interface OnboardingUserData {
  [key: string]: any
  active_step: number
  data_status: string
  has_applications: boolean
  has_documents: boolean
  has_funding_needs: boolean
  has_org: boolean
  has_profile: boolean
  has_scan_results: boolean
  debug_info: null
  email: string
  first_name: string
  is_onboarding_active: boolean
  is_wizard_dismissed: boolean
  last_name: string
  selected_org: SelectedOrg
  step_statuses: StepStatus[]
  timestamp: string
  user_uuid: string
}

interface OrgOnboardingData {
  uuid: string
  name: string
  profile_text: string | null
  created_at: string | null
  membership: string | null
  tier: number | null
  payment_type: string | null
  current_step: string
  has_docs: boolean
  has_profile: boolean
  has_funding_needs: boolean
  has_scan_results: boolean
  is_paid: boolean
  has_applications: boolean
  docs_count: number
  funding_needs_count: number
  scan_count: number
  applications_count: number
  owner_name: string | null
  owner_email: string | null
}

interface OrgOnboardingResponse {
  organisations: OrgOnboardingData[]
  admin_user: {
    uuid: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
  total_count: number
}

interface OnboardingData {
  data: OnboardingUserData[]
}

interface OnboardingStepData {
  name: string
  value: number
  percentage: string
  _totalReached?: number
  atStep?: number
  cumulativeCount?: number
  conversionRate?: string
  dropOffRate?: string
}

interface NewSubscriptionData {
  period: string
  displayPeriod: string
  newSubscriptions: number
}

const adminUrl = 'https://admin.fundsorter.com/'

function formatDate(value: string | null | undefined) {
  if (!value) return 'None'
  const date = new Date(value)
  const utcDate = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  )
  const formattedDate = `${utcDate.toLocaleString('en-NZ', {
    hour12: true,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Pacific/Auckland',
  })}`
  return formattedDate
}

function handleExportToCsv(
  data: OnboardingUserData[],
  columns: string[],
  columnNames: { [key: string]: string },
  csvTitle: string
) {
  const csvData = [
    [...Object.values(columnNames), 'User Link', 'Org Link'].join(','),
    // header row
  ]

  const columnsForCsv = [...columns, 'user_link', 'org_link'] // for linking to the user and org pages

  data.forEach((row) => {
    const rowData: string[] = []
    columnsForCsv.forEach((column) => {
      if (column === 'name') {
        rowData.push(`${row.first_name} ${row.last_name}`)
      } else if (column === 'selected_org.name') {
        rowData.push(
          row.selected_org && row.selected_org.name ? row.selected_org.name : ''
        )
      } else if (column === 'selected_org.created_at') {
        rowData.push(
          row.selected_org && row.selected_org.created_at
            ? `"${formatDate(row.selected_org.created_at)}"`
            : ''
        )
      } else if (column === 'selected_org.latest_scan_date') {
        rowData.push(
          row.selected_org && row.selected_org.latest_scan_date
            ? `"${formatDate(row.selected_org.latest_scan_date)}"`
            : 'No scans'
        )
      } else if (column === 'selected_org.membership') {
        rowData.push(
          row.selected_org && row.selected_org.membership
            ? row.selected_org.membership
            : ''
        )
      } else if (column === 'selected_org.tier') {
        rowData.push(getTierLabel(row.selected_org?.tier))
      } else if (column === 'is_wizard_dismissed') {
        rowData.push(row[column] ? 'Yes' : 'No')
      } else if (column === 'user_link') {
        rowData.push(`=HYPERLINK("${adminUrl}users/${row.user_uuid}")`)
      } else if (column === 'org_link') {
        rowData.push(
          row.selected_org
            ? `=HYPERLINK("${adminUrl}orgs/${row.selected_org.uuid}")`
            : ''
        )
      } else {
        rowData.push(row[column] ? row[column] : '')
      }
    })
    csvData.push(rowData.join(','))
  })

  const csvBlob = new Blob([csvData.join('\n')], { type: 'text/csv' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(csvBlob)
  link.download = `Onboarding Stats - ${csvTitle}.csv`
  link.click()
  URL.revokeObjectURL(link.href)
}

interface OnboardingDataTableProps {
  data: OnboardingUserData[]
  csvTitle: string
}

interface OrgDataTableProps {
  data: OrgOnboardingData[]
  csvTitle: string
  adminUser?: {
    uuid: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
}

function OnboardingDataTable({ data, csvTitle }: OnboardingDataTableProps) {
  const columnNames = {
    name: 'Name',
    'selected_org.name': 'Default Organisation',
    email: 'Email',
    is_wizard_dismissed: 'Wizard Dismissed',
    'selected_org.created_at': 'Org Signup Date',
    'selected_org.latest_scan_date': 'Latest Scan Date',
    'selected_org.membership': 'Membership',
    'selected_org.tier': 'Tier',
  }

  const columns = [
    'name',
    'selected_org.name',
    'email',
    'is_wizard_dismissed',
    'selected_org.created_at',
    'selected_org.latest_scan_date',
    'selected_org.membership',
    'selected_org.tier',
  ]

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleExportToCsv(data, columns, columnNames, csvTitle)}
      >
        Export to CSV
      </Button>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>
                  {columnNames[column as keyof typeof columnNames]}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={row.user_uuid}>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {column === 'name' ? (
                      <Link
                        to={`/users/${row.user_uuid}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >{`${row.first_name} ${row.last_name} `}</Link>
                    ) : column === 'selected_org.name' ? (
                      row.selected_org && (
                        <Link
                          to={`/orgs/${row.selected_org.uuid}`}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          {row.selected_org.name}
                        </Link>
                      )
                    ) : column === 'selected_org.created_at' ? (
                      row.selected_org &&
                      formatDate(row.selected_org.created_at)
                    ) : column === 'selected_org.latest_scan_date' ? (
                      row.selected_org &&
                      formatDate(row.selected_org.latest_scan_date)
                    ) : column === 'selected_org.membership' ? (
                      row.selected_org && row.selected_org.membership
                    ) : column === 'selected_org.tier' ? (
                      getTierLabel(row.selected_org?.tier)
                    ) : column === 'is_wizard_dismissed' ? (
                      row[column] ? (
                        'Yes'
                      ) : (
                        'No'
                      )
                    ) : (
                      row[column]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

function OrgDataTable({ data, csvTitle, adminUser }: OrgDataTableProps) {
  const columnNames = {
    name: 'Organisation Name',
    owner_name: 'Owner Name',
    owner_email: 'Owner Email',
    created_at: 'Created Date',
    current_step: 'Current Step',
    membership: 'Membership',
    tier: 'Tier',
    payment_type: 'Payment Type',
    docs_count: 'Documents',
    funding_needs_count: 'Funding Needs',
    scan_count: 'Scans',
    applications_count: 'Applications',
  }

  const columns = [
    'name',
    'owner_name',
    'owner_email',
    'created_at',
    'current_step',
    'membership',
    'tier',
    'payment_type',
    'docs_count',
    'funding_needs_count',
    'scan_count',
    'applications_count',
  ]

  const handleExportOrgToCsv = () => {
    const csvData = []

    // Add admin user info at the top if available
    if (adminUser) {
      csvData.push(
        `"Generated by: ${adminUser.first_name} ${adminUser.last_name} (${adminUser.email})"`
      )
      csvData.push('') // Empty line
    }

    csvData.push([...Object.values(columnNames), 'Org Link'].join(','))

    const columnsForCsv = [...columns, 'org_link']

    data.forEach((row) => {
      const rowData: string[] = []
      columnsForCsv.forEach((column) => {
        if (column === 'created_at') {
          rowData.push(row.created_at ? `"${formatDate(row.created_at)}"` : '')
        } else if (column === 'current_step') {
          rowData.push(
            row.current_step?.replace('step_', 'Step ').replace('_', ' ') || ''
          )
        } else if (column === 'tier') {
          rowData.push(getTierLabel(row.tier))
        } else if (column === 'payment_type') {
          rowData.push(row.payment_type || 'Free')
        } else if (column === 'owner_name') {
          rowData.push(row.owner_name || 'N/A')
        } else if (column === 'owner_email') {
          rowData.push(row.owner_email || 'N/A')
        } else if (column === 'org_link') {
          rowData.push(`=HYPERLINK("${adminUrl}orgs/${row.uuid}")`)
        } else {
          rowData.push(
            (row[column as keyof OrgOnboardingData] || '').toString()
          )
        }
      })
      csvData.push(rowData.join(','))
    })

    const csvBlob = new Blob([csvData.join('\n')], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(csvBlob)
    link.download = `Org Onboarding Stats - ${csvTitle}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleExportOrgToCsv}
      >
        Export to CSV
      </Button>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>
                  {columnNames[column as keyof typeof columnNames]}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.uuid}>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {column === 'name' ? (
                      <Link
                        to={`/orgs/${row.uuid}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {row.name}
                      </Link>
                    ) : column === 'created_at' ? (
                      row.created_at && formatDate(row.created_at)
                    ) : column === 'current_step' ? (
                      row.current_step
                        ?.replace('step_', 'Step ')
                        .replace('_', ' ')
                    ) : column === 'tier' ? (
                      getTierLabel(row.tier)
                    ) : column === 'payment_type' ? (
                      row.payment_type || 'Free'
                    ) : column === 'owner_name' ? (
                      row.owner_name || 'N/A'
                    ) : column === 'owner_email' ? (
                      row.owner_email || 'N/A'
                    ) : (
                      row[column as keyof OrgOnboardingData]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

interface UserDataTableProps {
  data: Array<{
    uuid: string
    auth0_id: string
    email: string
    first_name: string
    last_name: string
    created_at: string
  }>
  csvTitle: string
  adminUser?: {
    uuid: string | null
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
}

function UserDataTable({ data, csvTitle, adminUser }: UserDataTableProps) {
  const columnNames = {
    email: 'Email',
    first_name: 'First Name',
    last_name: 'Last Name',
    created_at: 'Signed Up Date',
  }

  const columns = ['email', 'first_name', 'last_name', 'created_at']

  const handleExportUsersToCsv = () => {
    const csvData = []

    // Add admin user info at the top if available
    if (adminUser) {
      csvData.push(
        `"Generated by: ${adminUser.first_name} ${adminUser.last_name} (${adminUser.email})"`
      )
      csvData.push('') // Empty line
    }

    csvData.push([...Object.values(columnNames), 'User Link'].join(','))

    const columnsForCsv = [...columns, 'user_link']

    data.forEach((row) => {
      const rowData: string[] = []
      columnsForCsv.forEach((column) => {
        if (column === 'created_at') {
          rowData.push(row.created_at ? `"${formatDate(row.created_at)}"` : '')
        } else if (column === 'user_link') {
          rowData.push(`=HYPERLINK("${adminUrl}users/${row.uuid}")`)
        } else if (
          column === 'email' ||
          column === 'first_name' ||
          column === 'last_name'
        ) {
          // Escape quotes in strings
          const value = row[column as keyof typeof row] || ''
          rowData.push(`"${value.toString().replace(/"/g, '""')}"`)
        } else {
          rowData.push((row[column as keyof typeof row] || '').toString())
        }
      })
      csvData.push(rowData.join(','))
    })

    const csvBlob = new Blob([csvData.join('\n')], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(csvBlob)
    link.download = `Users Without Organisations - ${csvTitle}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleExportUsersToCsv}
        sx={{ mb: 2 }}
      >
        Export to CSV
      </Button>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>
                  {columnNames[column as keyof typeof columnNames]}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.uuid}>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {column === 'email' ? (
                      <Link
                        to={`/users/${row.uuid}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {row.email}
                      </Link>
                    ) : column === 'created_at' ? (
                      row.created_at && formatDate(row.created_at)
                    ) : (
                      row[column as keyof typeof row]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

const COLOURS = [
  '#8B5CF6', // purple-500 for signed up
  '#7A288A', // purple
  '#FF0000', // red
  '#808080', // gray
  '#FF8042', // orange
  '#0088FE', // blue
  '#00C49F', // green
  '#FFBB28', // yellow for drafted application
]

interface OrgWithoutPayment {
  name: string
  created_at: string | null
}

interface NewSubscriptionsChartProps {
  data: NewSubscriptionData[]
  isLoading: boolean
  isError: boolean
  startDate?: string
  endDate?: string
  orgsWithoutPayments?: OrgWithoutPayment[]
}

function NewSubscriptionsChart({
  data,
  isLoading,
  isError,
  startDate,
  endDate,
  orgsWithoutPayments = [],
}: NewSubscriptionsChartProps) {
  const theme = useTheme()
  const [showOrgsDialog, setShowOrgsDialog] = useState(false)

  const formatTooltip = (value: number, name: string) => {
    return [value, 'New Subscriptions']
  }

  if (isLoading) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            New Subscriptions Analysis
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            New Subscriptions Analysis
          </Typography>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              Error loading new subscriptions data. Please try again.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  const totalNewSubscriptions = data.reduce(
    (sum, item) => sum + item.newSubscriptions,
    0
  )

  return (
    <>
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            New Subscriptions Analysis
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Shows organisations that became paid customers (first charge) in
            each time period.
            {startDate && endDate && (
              <>
                {' '}
                Showing data from {startDate} to {endDate}.
              </>
            )}
            {totalNewSubscriptions > 0 && (
              <> Total: {totalNewSubscriptions} new subscriptions.</>
            )}
          </Typography>

          {orgsWithoutPayments.length > 0 && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowOrgsDialog(true)}
                sx={{
                  textTransform: 'none',
                  padding: 0,
                  minWidth: 'auto',
                  fontWeight: 'normal',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {orgsWithoutPayments.length} org
                {orgsWithoutPayments.length > 1 ? 's were' : ' was'} included
                with no payment record
              </Button>
            </Typography>
          )}

          {data.length > 0 ? (
            <Box sx={{ height: 400, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="displayPeriod"
                    tick={{ fontSize: 12 }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    formatter={formatTooltip}
                    labelFormatter={(label: string) => `Period: ${label}`}
                  />
                  <Legend />
                  <Bar
                    dataKey="newSubscriptions"
                    fill={theme.palette.primary.main}
                    name="New Subscriptions"
                    shape={(props: any) => {
                      const { fill, x, y, width, height, payload } = props
                      // Use different colors for comparison periods
                      const fillColor = payload.is_comparison
                        ? theme.palette.grey[400]
                        : theme.palette.primary.main
                      return (
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={fillColor}
                        />
                      )
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No new subscriptions data available for the selected time period
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog for showing orgs without payments */}
      <Dialog
        open={showOrgsDialog}
        onClose={() => setShowOrgsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Organisations Without Payment Record
          <Typography variant="body2" color="textSecondary">
            These organisations have a payment type set but no charges recorded
            yet. Their creation date was used for the analysis.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <List>
            {orgsWithoutPayments.map((org, index) => {
              // Handle both string and object formats for backwards compatibility
              const isObject = typeof org === 'object' && org !== null
              const name = isObject ? org.name : org
              const date = isObject && org.created_at ? org.created_at : null

              return (
                <ListItem key={index}>
                  <ListItemText
                    primary={name}
                    secondary={
                      date
                        ? `Signed up: ${new Date(date).toLocaleDateString()}`
                        : ''
                    }
                  />
                </ListItem>
              )
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOrgsDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

interface LifecycleOrg {
  uuid: string
  name: string
  membership: string
  payment_type: string
  pause_start_date?: string
  pause_end_date?: string
  pause_reason?: string
  resumed_at?: string
  cancelled_at?: string
  reactivated_at?: string
  converted_at?: string
}

interface PauseResumeMetrics {
  total_paid_at_start: number
  total_paid_at_end: number
  paused_count: number
  resumed_count: number
  paused_percentage: number
  resumed_percentage: number
  cancelled_count?: number
  cancelled_percentage?: number
  reactivated_count?: number
  reactivated_percentage?: number
  new_paid_count?: number
  new_paid_percentage?: number
  paused_orgs?: LifecycleOrg[]
  resumed_orgs?: LifecycleOrg[]
  cancelled_orgs?: LifecycleOrg[]
  reactivated_orgs?: LifecycleOrg[]
  new_paid_orgs?: LifecycleOrg[]
}

interface MembershipLifecycleProps {
  pauseResumeMetrics?: PauseResumeMetrics
  comparisonPauseResumeMetrics?: PauseResumeMetrics[]
  startDate?: string
  endDate?: string
  comparePeriods?: number
  selectedPeriod?: string
  onLifecycleClick?: (
    type: 'paused' | 'resumed' | 'cancelled' | 'reactivated' | 'new_paid',
    orgs: LifecycleOrg[],
    title: string,
    periodStartDate?: string,
    periodEndDate?: string
  ) => void
}

function MembershipLifecycle({
  pauseResumeMetrics,
  comparisonPauseResumeMetrics = [],
  startDate,
  endDate,
  comparePeriods = 0,
  selectedPeriod,
  onLifecycleClick,
}: MembershipLifecycleProps) {
  // Helper to calculate comparison period dates
  const getComparisonPeriodDates = (periodIndex: number): { start: string; end: string } | null => {
    if (!startDate || !endDate) return null

    const start = new Date(startDate)
    const end = new Date(endDate)

    // Check if this is a calendar month (start is 1st, end is last day of month)
    const isCalendarMonth =
      start.getDate() === 1 &&
      end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate() &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()

    let periodStart: Date
    let periodEnd: Date

    if (isCalendarMonth) {
      // For calendar months, go back by months
      periodStart = new Date(start.getFullYear(), start.getMonth() - (periodIndex + 1), 1)
      periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, 0)
    } else {
      // For custom ranges, calculate based on day difference
      const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
      const offset = daysDiff * (periodIndex + 1)
      periodStart = new Date(start)
      periodStart.setDate(start.getDate() - offset)
      periodEnd = new Date(end)
      periodEnd.setDate(end.getDate() - offset)
    }

    // Format dates in local timezone to avoid UTC conversion issues
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      start: formatLocalDate(periodStart),
      end: formatLocalDate(periodEnd),
    }
  }

  // Format date range for display (e.g., "Oct 1 - Oct 31")
  const formatDateRange = (start: string, end: string): string => {
    const startDate = new Date(start + 'T00:00:00')
    const endDate = new Date(end + 'T00:00:00')
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const startMonth = months[startDate.getMonth()]
    const endMonth = months[endDate.getMonth()]
    const startDay = startDate.getDate()
    const endDay = endDate.getDate()

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}`
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
  }

  // Generate period labels
  const getPeriodLabel = (periodIndex: number): string => {
    const dates = getComparisonPeriodDates(periodIndex)
    if (!dates) return `Period ${periodIndex + 1}`
    return formatDateRange(dates.start, dates.end)
  }

  if (!pauseResumeMetrics) return null

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Membership Lifecycle
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Percentage metrics are relative to paid accounts at period start
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Metric</TableCell>
                <TableCell align="center" colSpan={2}>
                  {startDate && endDate ? formatDateRange(startDate, endDate) : 'Current Period'}
                </TableCell>
                {Array.from({ length: comparePeriods }).map((_, i) => (
                  <TableCell key={`header-period-${i}`} align="center" colSpan={2}>
                    {getPeriodLabel(i)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Paid accounts at start */}
              {pauseResumeMetrics.total_paid_at_start > 0 && (
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Paid accounts at start
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {pauseResumeMetrics.total_paid_at_start}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  </TableCell>
                  {comparisonPauseResumeMetrics.map((metrics, periodIndex) => (
                    <React.Fragment key={`paid-base-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {metrics?.total_paid_at_start ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              )}

              {/* Paused Row */}
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#FFA726',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Paused in period
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      cursor: pauseResumeMetrics.paused_count > 0 ? 'pointer' : 'default',
                      '&:hover': pauseResumeMetrics.paused_count > 0 ? { color: 'primary.main' } : {},
                    }}
                    onClick={() =>
                      pauseResumeMetrics.paused_count > 0 &&
                      onLifecycleClick?.(
                        'paused',
                        pauseResumeMetrics.paused_orgs || [],
                        'Paused Organisations'
                      )
                    }
                  >
                    {pauseResumeMetrics.paused_count}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={pauseResumeMetrics.paused_percentage > 5 ? 'warning.main' : 'text.secondary'}
                    sx={{ fontWeight: 'medium' }}
                  >
                    {pauseResumeMetrics.paused_percentage}%
                  </Typography>
                </TableCell>
                {comparisonPauseResumeMetrics.map((metrics, periodIndex) => {
                  const periodDates = getComparisonPeriodDates(periodIndex)
                  return (
                    <React.Fragment key={`paused-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            cursor: (metrics?.paused_count ?? 0) > 0 ? 'pointer' : 'default',
                            '&:hover': (metrics?.paused_count ?? 0) > 0 ? { color: 'primary.main' } : {},
                          }}
                          onClick={() =>
                            (metrics?.paused_count ?? 0) > 0 &&
                            onLifecycleClick?.(
                              'paused',
                              metrics?.paused_orgs || [],
                              'Paused Organisations',
                              periodDates?.start,
                              periodDates?.end
                            )
                          }
                        >
                          {metrics?.paused_count ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {metrics?.paused_percentage != null ? `${metrics.paused_percentage}%` : '-'}
                        </Typography>
                      </TableCell>
                    </React.Fragment>
                  )
                })}
              </TableRow>

              {/* Resumed Row */}
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#66BB6A',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Resumed in period
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      cursor: pauseResumeMetrics.resumed_count > 0 ? 'pointer' : 'default',
                      '&:hover': pauseResumeMetrics.resumed_count > 0 ? { color: 'primary.main' } : {},
                    }}
                    onClick={() =>
                      pauseResumeMetrics.resumed_count > 0 &&
                      onLifecycleClick?.(
                        'resumed',
                        pauseResumeMetrics.resumed_orgs || [],
                        'Resumed Organisations'
                      )
                    }
                  >
                    {pauseResumeMetrics.resumed_count}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: 'medium' }}
                  >
                    {pauseResumeMetrics.resumed_percentage}%
                  </Typography>
                </TableCell>
                {comparisonPauseResumeMetrics.map((metrics, periodIndex) => {
                  const periodDates = getComparisonPeriodDates(periodIndex)
                  return (
                    <React.Fragment key={`resumed-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            cursor: (metrics?.resumed_count ?? 0) > 0 ? 'pointer' : 'default',
                            '&:hover': (metrics?.resumed_count ?? 0) > 0 ? { color: 'primary.main' } : {},
                          }}
                          onClick={() =>
                            (metrics?.resumed_count ?? 0) > 0 &&
                            onLifecycleClick?.(
                              'resumed',
                              metrics?.resumed_orgs || [],
                              'Resumed Organisations',
                              periodDates?.start,
                              periodDates?.end
                            )
                          }
                        >
                          {metrics?.resumed_count ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {metrics?.resumed_percentage != null ? `${metrics.resumed_percentage}%` : '-'}
                        </Typography>
                      </TableCell>
                    </React.Fragment>
                  )
                })}
              </TableRow>

              {/* Cancelled Row */}
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#EF5350',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Cancelled in period
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      cursor: (pauseResumeMetrics.cancelled_count ?? 0) > 0 ? 'pointer' : 'default',
                      '&:hover': (pauseResumeMetrics.cancelled_count ?? 0) > 0 ? { color: 'primary.main' } : {},
                    }}
                    onClick={() =>
                      (pauseResumeMetrics.cancelled_count ?? 0) > 0 &&
                      onLifecycleClick?.(
                        'cancelled',
                        pauseResumeMetrics?.cancelled_orgs || [],
                        'Cancelled Organisations'
                      )
                    }
                  >
                    {pauseResumeMetrics.cancelled_count ?? 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color="error.main"
                    sx={{ fontWeight: 'medium' }}
                  >
                    {pauseResumeMetrics.cancelled_percentage ?? 0}%
                  </Typography>
                </TableCell>
                {comparisonPauseResumeMetrics.map((metrics, periodIndex) => {
                  const periodDates = getComparisonPeriodDates(periodIndex)
                  const compCancelledCount = metrics?.cancelled_count ?? 0
                  const compCancelledOrgs = metrics?.cancelled_orgs || []
                  return (
                    <React.Fragment key={`cancelled-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            cursor: compCancelledCount > 0 ? 'pointer' : 'default',
                            '&:hover': compCancelledCount > 0 ? { color: 'primary.main' } : {},
                          }}
                          onClick={() =>
                            compCancelledCount > 0 &&
                            onLifecycleClick?.(
                              'cancelled',
                              compCancelledOrgs,
                              'Cancelled Organisations',
                              periodDates?.start,
                              periodDates?.end
                            )
                          }
                        >
                          {compCancelledCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {metrics?.cancelled_percentage != null ? `${metrics.cancelled_percentage}%` : '-'}
                        </Typography>
                      </TableCell>
                    </React.Fragment>
                  )
                })}
              </TableRow>

              {/* Reactivated Row */}
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#4CAF50',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Reactivated in period
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      cursor: (pauseResumeMetrics.reactivated_count ?? 0) > 0 ? 'pointer' : 'default',
                      '&:hover': (pauseResumeMetrics.reactivated_count ?? 0) > 0 ? { color: 'primary.main' } : {},
                    }}
                    onClick={() =>
                      (pauseResumeMetrics.reactivated_count ?? 0) > 0 &&
                      onLifecycleClick?.(
                        'reactivated',
                        pauseResumeMetrics?.reactivated_orgs || [],
                        'Reactivated Organisations'
                      )
                    }
                  >
                    {pauseResumeMetrics.reactivated_count ?? 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: 'medium' }}
                  >
                    {pauseResumeMetrics.reactivated_percentage ?? 0}%
                  </Typography>
                </TableCell>
                {comparisonPauseResumeMetrics.map((metrics, periodIndex) => {
                  const periodDates = getComparisonPeriodDates(periodIndex)
                  const compReactivatedCount = metrics?.reactivated_count ?? 0
                  const compReactivatedOrgs = metrics?.reactivated_orgs || []
                  return (
                    <React.Fragment key={`reactivated-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            cursor: compReactivatedCount > 0 ? 'pointer' : 'default',
                            '&:hover': compReactivatedCount > 0 ? { color: 'primary.main' } : {},
                          }}
                          onClick={() =>
                            compReactivatedCount > 0 &&
                            onLifecycleClick?.(
                              'reactivated',
                              compReactivatedOrgs,
                              'Reactivated Organisations',
                              periodDates?.start,
                              periodDates?.end
                            )
                          }
                        >
                          {compReactivatedCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {metrics?.reactivated_percentage != null ? `${metrics.reactivated_percentage}%` : '-'}
                        </Typography>
                      </TableCell>
                    </React.Fragment>
                  )
                })}
              </TableRow>

              {/* New Paid Row */}
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#2196F3',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      New paid in period
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'medium',
                      cursor: (pauseResumeMetrics.new_paid_count ?? 0) > 0 ? 'pointer' : 'default',
                      '&:hover': (pauseResumeMetrics.new_paid_count ?? 0) > 0 ? { color: 'primary.main' } : {},
                    }}
                    onClick={() =>
                      (pauseResumeMetrics.new_paid_count ?? 0) > 0 &&
                      onLifecycleClick?.(
                        'new_paid',
                        pauseResumeMetrics?.new_paid_orgs || [],
                        'New Paid Organisations'
                      )
                    }
                  >
                    {pauseResumeMetrics.new_paid_count ?? 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: 'medium' }}
                  >
                    {pauseResumeMetrics.new_paid_percentage ?? 0}%
                  </Typography>
                </TableCell>
                {comparisonPauseResumeMetrics.map((metrics, periodIndex) => {
                  const periodDates = getComparisonPeriodDates(periodIndex)
                  const compNewPaidCount = metrics?.new_paid_count ?? 0
                  const compNewPaidOrgs = metrics?.new_paid_orgs || []
                  return (
                    <React.Fragment key={`new-paid-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            cursor: compNewPaidCount > 0 ? 'pointer' : 'default',
                            '&:hover': compNewPaidCount > 0 ? { color: 'primary.main' } : {},
                          }}
                          onClick={() =>
                            compNewPaidCount > 0 &&
                            onLifecycleClick?.(
                              'new_paid',
                              compNewPaidOrgs,
                              'New Paid Organisations',
                              periodDates?.start,
                              periodDates?.end
                            )
                          }
                        >
                          {compNewPaidCount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {metrics?.new_paid_percentage != null ? `${metrics.new_paid_percentage}%` : '-'}
                        </Typography>
                      </TableCell>
                    </React.Fragment>
                  )
                })}
              </TableRow>

              {/* Paid accounts at end */}
              {pauseResumeMetrics.total_paid_at_end > 0 && (
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Paid accounts at end
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {pauseResumeMetrics.total_paid_at_end}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">-</Typography>
                  </TableCell>
                  {comparisonPauseResumeMetrics.map((metrics, periodIndex) => (
                    <React.Fragment key={`paid-end-period-${periodIndex}`}>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {metrics?.total_paid_at_end ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

interface OnboardingStepSummaryProps {
  data: Array<{
    name: string
    value: number
    percentage: string
    _totalReached?: number
    atStep?: number
  }>
  totalUsers: number
  totalOrgs: number
  comparePeriods?: number
  onCountClick?: (stepIndex: number, title: string, csvTitle: string) => void
  userBreakdown?: {
    users_no_org: number
    users_created_org: number
    users_added_to_org: number
  }
  comparisonUserBreakdowns?: Array<{
    users_no_org: number
    users_created_org: number
    users_added_to_org: number
  }>
}

function OnboardingStepSummary({
  data,
  totalUsers,
  totalOrgs,
  comparePeriods = 0,
  startDate,
  endDate,
  comparisonData = [],
  comparisonUserCounts = [],
  selectedPeriod,
  onCountClick,
  userBreakdown,
  comparisonUserBreakdowns = [],
}: OnboardingStepSummaryProps & {
  startDate?: string
  endDate?: string
  comparisonData?: Array<
    Array<{
      value: number
      cumulativeCount: number
      conversionRate: string
      atStep?: number
    }>
  >
  comparisonUserCounts?: number[]
  selectedPeriod?: string
}) {
  // Helper function to get smart period labels
  const getPeriodLabels = () => {
    if (!startDate || !endDate) {
      return {
        current: 'Current Period',
        periods: Array.from(
          { length: comparePeriods },
          (_, i) => `${i + 1} Period${i === 0 ? '' : 's'} Ago`
        ),
      }
    }

    // Use selectedPeriod if available to determine the appropriate label
    let currentLabel = ''
    let periodType = 'days'

    if (selectedPeriod) {
      switch (selectedPeriod) {
        case '7days':
          currentLabel = 'Last 7 Days'
          periodType = 'week'
          break
        case '30days':
          currentLabel = 'Last 30 Days'
          periodType = 'month'
          break
        case '90days':
          currentLabel = 'Last 90 Days'
          periodType = 'quarter'
          break
        case 'thisweek':
          currentLabel = 'This Week'
          periodType = 'week'
          break
        case 'lastweek':
          currentLabel = 'Last Week'
          periodType = 'week'
          break
        case 'thismonth':
          currentLabel = 'This Month'
          periodType = 'month'
          break
        case 'lastmonth':
          currentLabel = 'Last Month'
          periodType = 'month'
          break
        case 'thisquarter':
          currentLabel = 'This Quarter'
          periodType = 'quarter'
          break
        case 'lastquarter':
          currentLabel = 'Last Quarter'
          periodType = 'quarter'
          break
        default:
          currentLabel = 'Current Period'
          periodType = 'days'
      }
    } else {
      // Show actual date range when no period is selected
      if (startDate && endDate) {
        const formatDisplayDate = (dateStr: string) => {
          const date = new Date(dateStr)
          return date.toLocaleDateString('en-NZ', {
            month: 'short',
            day: 'numeric',
          })
        }
        currentLabel = `${formatDisplayDate(startDate)} - ${formatDisplayDate(
          endDate
        )}`
      } else {
        currentLabel = 'Custom Period'
      }

      // Fallback to automatic detection based on date range for period type
      const start = new Date(startDate)
      const end = new Date(endDate)
      const diffDays = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
      )

      if (diffDays <= 8) {
        periodType = 'week'
      } else if (diffDays <= 35) {
        periodType = 'month'
      } else if (diffDays <= 100) {
        periodType = 'quarter'
      } else {
        periodType = 'year'
      }
    }

    // Generate comparison period dates
    const comparisonPeriods = []
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffDays = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    )

    // Check if the selected period is a calendar month
    // (starts on 1st and ends on last day of same month)
    const isFirstOfMonth = start.getDate() === 1
    const isSameMonth =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
    const nextDay = new Date(end)
    nextDay.setDate(nextDay.getDate() + 1)
    const isLastDayOfMonth = nextDay.getDate() === 1
    const isCalendarMonth = isFirstOfMonth && isSameMonth && isLastDayOfMonth

    for (let i = 1; i <= comparePeriods; i++) {
      let periodStart: Date
      let periodEnd: Date

      if (isCalendarMonth) {
        // Use calendar month alignment
        const targetMonth = start.getMonth() - i
        const targetYear = start.getFullYear() + Math.floor(targetMonth / 12)
        const normalizedMonth = ((targetMonth % 12) + 12) % 12

        // First day of that month
        periodStart = new Date(targetYear, normalizedMonth, 1)
        // Last day of that month (first of next month minus 1 day)
        periodEnd = new Date(targetYear, normalizedMonth + 1, 0)
      } else {
        // Use simple day-based subtraction for non-month periods
        periodStart = new Date(start)
        periodEnd = new Date(end)
        const inclusiveDays = diffDays + 1
        const shiftDays = inclusiveDays * i
        periodStart.setDate(periodStart.getDate() - shiftDays)
        periodEnd.setDate(periodEnd.getDate() - shiftDays)
      }

      const formatDate = (date: Date) => {
        return date
          .toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: periodType === 'year' ? 'numeric' : undefined,
          })
          .replace(',', '')
      }

      comparisonPeriods.push(
        `${formatDate(periodStart)} - ${formatDate(periodEnd)}`
      )
    }

    return {
      current: currentLabel,
      periods: comparisonPeriods,
    }
  }

  const periodLabels = getPeriodLabels()

  // Calculate cumulative conversion rates (how many organisations reach each step)
  const cumulativeData = data.map((item, index) => {
    // Special handling for different steps
    let cumulativeCount

    if (
      item.name === 'Only Completed Step 6 - Converted to Paid' &&
      item._totalReached !== undefined
    ) {
      // Use the total_reached value from backend for converted to paid
      cumulativeCount = item._totalReached
    } else if (index === 0) {
      // First step (Setup Org) - all orgs reached this step
      cumulativeCount = totalOrgs
    } else {
      // For cumulative, we sum all organisations who reached this step or beyond
      cumulativeCount = data
        .slice(index)
        .reduce((sum, step) => sum + step.value, 0)
    }

    // All conversion rates are based on total orgs
    const conversionRate =
      totalOrgs > 0
        ? ((cumulativeCount / totalOrgs) * 100).toFixed(1)
        : '0.0'

    return {
      ...item,
      cumulativeCount,
      conversionRate,
      dropOffRate:
        index > 0 && totalOrgs > 0
          ? (
              (data.slice(0, index).reduce((sum, step) => sum + step.value, 0) /
                totalOrgs) *
              100
            ).toFixed(1)
          : '0.0',
      atStep: item.atStep,
    }
  })

  // comparisonData is now passed as a prop from the parent component

  const getHeaderColumns = () => {
    const baseColumns = [
      { key: 'step', label: 'Step', align: 'left' as const, group: 'step' },
      {
        key: 'current-orgs',
        label: 'Orgs',
        align: 'right' as const,
        group: 'current',
      },
      {
        key: 'current-conversion',
        label: 'Conv %',
        align: 'right' as const,
        group: 'current',
      },
    ]

    const comparisonColumns = []
    for (let i = 0; i < comparePeriods; i++) {
      comparisonColumns.push(
        {
          key: `period-${i + 1}-orgs`,
          label: 'Orgs',
          align: 'right' as const,
          group: `period-${i + 1}`,
        },
        {
          key: `period-${i + 1}-conversion`,
          label: 'Conv %',
          align: 'right' as const,
          group: `period-${i + 1}`,
        }
      )
    }

    return [...baseColumns, ...comparisonColumns]
  }

  const headerColumns = getHeaderColumns()

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Onboarding Funnel Analysis
          {comparePeriods > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              sx={{ ml: 1 }}
            >
              (vs {comparePeriods} previous period
              {comparePeriods > 1 ? 's' : ''})
            </Typography>
          )}
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              {/* First Header Row - Period Labels */}
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    verticalAlign: 'bottom',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <strong>Onboarding Step</strong>
                </TableCell>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <strong>{periodLabels.current}</strong>
                </TableCell>
                {periodLabels.periods.map((periodLabel, i) => (
                  <TableCell
                    key={`period-header-${i}`}
                    colSpan={2}
                    align="center"
                    sx={{
                      bgcolor: 'grey.100',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.8rem',
                    }}
                  >
                    <strong>{periodLabel}</strong>
                  </TableCell>
                ))}
              </TableRow>

              {/* Second Header Row - Column Labels */}
              <TableRow>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '0.75rem',
                  }}
                >
                  <strong>Orgs at Step</strong>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '0.75rem',
                  }}
                >
                  <strong>Conversion %</strong>
                </TableCell>
                {Array.from({ length: comparePeriods }).map((_, i) => (
                  <React.Fragment key={`period-headers-${i}`}>
                    <TableCell
                      key={`period-${i}-orgs`}
                      align="right"
                      sx={{ bgcolor: 'grey.100', fontSize: '0.75rem' }}
                    >
                      <strong>Orgs</strong>
                    </TableCell>
                    <TableCell
                      key={`period-${i}-conv`}
                      align="right"
                      sx={{ bgcolor: 'grey.100', fontSize: '0.75rem' }}
                    >
                      <strong>Conv %</strong>
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* User Breakdown Section */}
              {userBreakdown && (
                <>
                  <TableRow sx={{ bgcolor: 'info.lighter' }}>
                    <TableCell colSpan={100}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        User Statistics (Sign-ups in this period)
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Total sign-ups
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {userBreakdown.users_no_org + userBreakdown.users_created_org + userBreakdown.users_added_to_org}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    </TableCell>
                    {comparisonData.map((_, periodIndex) => {
                      const breakdown = comparisonUserBreakdowns[periodIndex]
                      const total = breakdown
                        ? breakdown.users_no_org + breakdown.users_created_org + breakdown.users_added_to_org
                        : null
                      return (
                        <React.Fragment key={`total-period-${periodIndex}`}>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {total !== null ? total : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          </TableCell>
                        </React.Fragment>
                      )
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No org yet (in onboarding)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {userBreakdown.users_no_org}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    </TableCell>
                    {comparisonData.map((_, periodIndex) => (
                      <React.Fragment key={`no-org-period-${periodIndex}`}>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {comparisonUserBreakdowns[periodIndex]?.users_no_org || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Created an org
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {userBreakdown.users_created_org}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    </TableCell>
                    {comparisonData.map((_, periodIndex) => (
                      <React.Fragment key={`created-org-period-${periodIndex}`}>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {comparisonUserBreakdowns[periodIndex]?.users_created_org || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Added to existing org (team member)
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {userBreakdown.users_added_to_org}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    </TableCell>
                    {comparisonData.map((_, periodIndex) => (
                      <React.Fragment key={`added-org-period-${periodIndex}`}>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {comparisonUserBreakdowns[periodIndex]?.users_added_to_org || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={100}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Organization Onboarding Funnel (conversion % based on orgs created)
                      </Typography>
                    </TableCell>
                  </TableRow>
                </>
              )}
              {cumulativeData
                .filter((row) => row.name !== 'Cancelled Membership')
                .map((row, index) => (
                <TableRow
                  key={row.name}
                  sx={{ '&:hover': { bgcolor: 'grey.50' } }}
                >
                  {/* Step Name */}
                  <TableCell sx={{ minWidth: 160 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: COLOURS[index % COLOURS.length],
                          borderRadius: '50%',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'medium', lineHeight: 1.2 }}
                      >
                        {row.name
                          .replace(/^Only Completed Step \d+ - /, '')
                          .replace('Completed Onboarding - ', '')}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Current Period Data */}
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                            // Make Setup Org cumulative count clickable
                            ...(row.name === 'Only Completed Step 1 - Setup Org'
                              ? {
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                  },
                                }
                              : {}),
                          }}
                          onClick={
                            row.name === 'Only Completed Step 1 - Setup Org'
                              ? () =>
                                  onCountClick?.(
                                    index,
                                    'All Organisations Created',
                                    'All Organisations'
                                  )
                              : undefined
                          }
                        >
                          {row.cumulativeCount}
                        </Typography>
                        {(row.atStep !== undefined &&
                          row.atStep !== null &&
                          row.atStep > 0) ||
                        row.value > 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              cursor: 'pointer',
                              '&:hover': { color: 'primary.main' },
                            }}
                            onClick={() =>
                              onCountClick?.(
                                index,
                                row.name === 'Cancelled Membership'
                                  ? `${row.name}`
                                  : `${row.name} - At Step Only`,
                                row.name === 'Cancelled Membership'
                                  ? `${row.name}`
                                  : `${row.name} - At Step Only`
                              )
                            }
                          >
                            (
                            {row.atStep !== undefined && row.atStep !== null
                              ? row.atStep
                              : row.value}{' '}
                            at step)
                          </Typography>
                        ) : null}
                      </Box>
                      {comparisonData.length > 0 &&
                        (() => {
                          const compareCountValue =
                            comparisonData[0][index].cumulativeCount
                          const countTrend =
                            row.cumulativeCount > compareCountValue
                              ? 'up'
                              : row.cumulativeCount < compareCountValue
                              ? 'down'
                              : 'same'
                          return (
                            <>
                              {countTrend === 'up' && (
                                <Typography
                                  color="success.main"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  ↗
                                </Typography>
                              )}
                              {countTrend === 'down' && (
                                <Typography
                                  color="error.main"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  ↘
                                </Typography>
                              )}
                            </>
                          )
                        })()}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 80 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color={
                          parseFloat(row.conversionRate) >= 50
                            ? 'success.main'
                            : parseFloat(row.conversionRate) >= 25
                            ? 'warning.main'
                            : 'error.main'
                        }
                        sx={{ fontWeight: 'bold' }}
                      >
                        {row.conversionRate}%
                      </Typography>
                      {comparisonData.length > 0 &&
                        (() => {
                          const compareConversionValue = parseFloat(
                            comparisonData[0][index].conversionRate
                          )
                          const conversionTrend =
                            parseFloat(row.conversionRate) >
                            compareConversionValue
                              ? 'up'
                              : parseFloat(row.conversionRate) <
                                compareConversionValue
                              ? 'down'
                              : 'same'
                          return (
                            <>
                              {conversionTrend === 'up' && (
                                <Typography
                                  color="success.main"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  ↗
                                </Typography>
                              )}
                              {conversionTrend === 'down' && (
                                <Typography
                                  color="error.main"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  ↘
                                </Typography>
                              )}
                            </>
                          )
                        })()}
                    </Box>
                  </TableCell>

                  {/* Comparison Period Data */}
                  {comparisonData.map((periodData, periodIndex) => {
                    // Calculate trends compared to the next period (to the right)
                    const showTrends = periodIndex < comparisonData.length - 1

                    // For conversion rate trend - compare to the next period
                    const currentConversionValue = parseFloat(
                      periodData[index].conversionRate
                    )
                    const nextConversionValue = showTrends
                      ? parseFloat(
                          comparisonData[periodIndex + 1][index].conversionRate
                        )
                      : 0
                    const conversionTrend = showTrends
                      ? currentConversionValue > nextConversionValue
                        ? 'up'
                        : currentConversionValue < nextConversionValue
                        ? 'down'
                        : 'same'
                      : 'same'

                    // For absolute count trend - compare to the next period
                    const currentCountValue = periodData[index].cumulativeCount
                    const nextCountValue = showTrends
                      ? comparisonData[periodIndex + 1][index].cumulativeCount
                      : 0
                    const countTrend = showTrends
                      ? currentCountValue > nextCountValue
                        ? 'up'
                        : currentCountValue < nextCountValue
                        ? 'down'
                        : 'same'
                      : 'same'

                    return (
                      <React.Fragment key={`period-${periodIndex}-data`}>
                        <TableCell
                          key={`period-${periodIndex}-orgs`}
                          align="right"
                          sx={{ minWidth: 70 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {periodData[index].cumulativeCount}
                              </Typography>
                              {(periodData[index].atStep !== undefined &&
                                periodData[index].atStep !== null &&
                                periodData[index].atStep > 0) ||
                              periodData[index].value > 0 ? (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ display: 'block', fontSize: '0.7rem' }}
                                >
                                  (
                                  {periodData[index].atStep !== undefined &&
                                  periodData[index].atStep !== null
                                    ? periodData[index].atStep
                                    : periodData[index].value}
                                  )
                                </Typography>
                              ) : null}
                            </Box>
                            {showTrends && countTrend === 'up' && (
                              <Typography
                                color="success.main"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                ↗
                              </Typography>
                            )}
                            {showTrends && countTrend === 'down' && (
                              <Typography
                                color="error.main"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                ↘
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          key={`period-${periodIndex}-conv`}
                          align="right"
                          sx={{ minWidth: 70 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {periodData[index].conversionRate}%
                            </Typography>
                            {showTrends && conversionTrend === 'up' && (
                              <Typography
                                color="success.main"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                ↗
                              </Typography>
                            )}
                            {showTrends && conversionTrend === 'down' && (
                              <Typography
                                color="error.main"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                ↘
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </React.Fragment>
                    )
                  })}
                </TableRow>
              ))}

            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            • <strong>Orgs at Step:</strong> Total organisations that reached
            this step (with orgs currently at step in parentheses) <br /> •{' '}
            <strong>Conversion %:</strong> Percentage of all organisations that
            reached this step <br />• <strong>Arrows:</strong> ↗ indicates
            improvement, ↘ indicates decline from previous period
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default function OnboardingStatsDashboard() {
  const apiUrl = useApiUrl()
  const { getAccessTokenSilently } = useAuth0()

  const [totalUsers, setTotalUsers] = useState(0)
  const [totalUsersFromOrgStats, setTotalUsersFromOrgStats] = useState(0)

  // Date filter state
  // Set default to last 7 days
  const getDefaultDates = () => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(today.getDate() - 6) // Changed from -7 to -6 to get exactly 7 days

    // Format dates in local timezone to avoid UTC conversion issues
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      start: formatLocalDate(sevenDaysAgo),
      end: formatLocalDate(today),
    }
  }

  const defaultDates = getDefaultDates()
  const [startDate, setStartDate] = useState(defaultDates.start)
  const [endDate, setEndDate] = useState(defaultDates.end)
  const [appliedFilters, setAppliedFilters] = useState<{
    start_date?: string
    end_date?: string
  }>({
    start_date: defaultDates.start,
    end_date: defaultDates.end,
  })
  const [comparePeriods, setComparePeriods] = useState<number>(5)
  const [selectedPeriod, setSelectedPeriod] = useState<string>('7days')

  // URL search params for tab state
  const [searchParams, setSearchParams] = useSearchParams()

  // Analysis view state - synced with URL
  const analysisView = (searchParams.get('view') as 'cohort' | 'date') || 'cohort'
  const setAnalysisView = (view: 'cohort' | 'date') => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      newParams.set('view', view)
      return newParams
    })
  }

  // Comparison data state
  type ComparisonStepData = {
    value: number
    cumulativeCount: number
    conversionRate: string
  }
  const [comparisonData, setComparisonData] = useState<ComparisonStepData[][]>(
    []
  )

  // Popup state for detailed view
  const [popupOpen, setPopupOpen] = useState(false)
  const [popupData, setPopupData] = useState<{
    data: OnboardingUserData[] | OrgOnboardingData[]
    title: string
    csvTitle: string
    dataType: 'user' | 'org' | 'users-without-orgs'
    adminUser?: {
      uuid: string | null
      first_name: string | null
      last_name: string | null
      email: string | null
    } | null
  }>({ data: [], title: '', csvTitle: '', dataType: 'user' })

  // Lifecycle orgs dialog state (paused/resumed/cancelled/reactivated/new_paid)
  const [lifecycleDialogOpen, setLifecycleDialogOpen] = useState(false)
  const [lifecycleData, setLifecycleData] = useState<{
    type: 'paused' | 'resumed' | 'cancelled' | 'reactivated' | 'new_paid'
    orgs: LifecycleOrg[]
    title: string
  }>({ type: 'paused', orgs: [], title: '' })

  // Handler for lifecycle clicks
  const handleLifecycleClick = (
    type: 'paused' | 'resumed' | 'cancelled' | 'reactivated' | 'new_paid',
    orgs: LifecycleOrg[],
    title: string,
    periodStartDate?: string,
    periodEndDate?: string
  ) => {
    // Use passed dates if available, otherwise fall back to current period filters
    const startDate = periodStartDate || appliedFilters.start_date
    const endDate = periodEndDate || appliedFilters.end_date
    const dateRangeString =
      startDate && endDate
        ? `${startDate} to ${endDate}`
        : 'All Time'
    setLifecycleData({
      type,
      orgs,
      title: `${title} (${dateRangeString})`,
    })
    setLifecycleDialogOpen(true)
  }

  // Function to open popup with step data
  const handleCountClick = async (
    stepIndex: number,
    title: string,
    csvTitle: string
  ) => {
    const dateRangeString =
      appliedFilters.start_date && appliedFilters.end_date
        ? `${appliedFilters.start_date} to ${appliedFilters.end_date}`
        : 'All Time'

    const titleWithDateRange = `${title} (${dateRangeString})`

    // Map step index to step name
    // Note: The table data starts with step_1_setup_org at index 0, so we don't include step_0_signed_up
    const stepNames = [
      'step_1_setup_org',
      'step_2_upload_docs',
      'step_3_org_profile',
      'step_4_funding_request',
      'step_5_scan_matches',
      'step_6_converted_to_paid',
      'completed_onboarding',
      'cancelled',
    ]

    const stepName = stepNames[stepIndex]

    // For "Signed Up - At Step Only", we need to show users without organisations
    const isSignedUpAtStep =
      stepName === 'step_0_signed_up' && title.includes('At Step Only')

    // For "All Organisations Created", show all orgs without step filter
    const isAllOrgs = title === 'All Organisations Created'

    // For "All Organisations Cancelled", show all orgs without step filter
    const isCancelled =
      stepName === 'cancelled' && title === 'Cancelled Membership'

    try {
      if (isSignedUpAtStep) {
        // Fetch users without organisations
        const queryParams = new URLSearchParams()
        if (appliedFilters.start_date)
          queryParams.append('start_date', appliedFilters.start_date)
        if (appliedFilters.end_date)
          queryParams.append('end_date', appliedFilters.end_date)

        const token = await getAccessTokenSilently()
        const response = await axios.get<{ users: any[]; admin_user: any }>(
          `/users/users-without-orgs?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: 30000,
          }
        )

        setPopupData({
          data: response.data.users,
          title: titleWithDateRange,
          csvTitle: csvTitle,
          dataType: 'users-without-orgs',
          adminUser: response.data.admin_user,
        })
        setPopupOpen(true)
      } else {
        // Build query parameters
        const queryParams = new URLSearchParams()
        if (appliedFilters.start_date)
          queryParams.append('start_date', appliedFilters.start_date)
        if (appliedFilters.end_date)
          queryParams.append('end_date', appliedFilters.end_date)

        // Only add step parameter if not showing all orgs
        if (!isAllOrgs && stepName) {
          queryParams.append('step', stepName)
        }

        // Add at_step_only flag if this is an "at step" click
        if (title.includes('At Step Only')) {
          queryParams.append('at_step_only', 'true')
        }

        // Get fresh token and call the API
        const token = await getAccessTokenSilently()
        const response = await axios.get<OrgOnboardingResponse>(
          `/users/onboarding-org-details?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            timeout: 30000,
          }
        )

        const responseData: OrgOnboardingResponse = response.data

        setPopupData({
          data: responseData.organisations,
          title: titleWithDateRange,
          csvTitle: csvTitle,
          dataType: 'org',
          adminUser: responseData.admin_user,
        })
        setPopupOpen(true)
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.status === 403
          ? 'Access denied - insufficient permissions'
          : error.response?.status === 401
          ? 'Authentication required'
          : 'Error loading organisation data'

      setPopupData({
        data: [],
        title: `${titleWithDateRange} - ${errorMessage}`,
        csvTitle: csvTitle,
        dataType: 'org',
      })
      setPopupOpen(true)
    }
  }

  // Filter handlers
  const handleApplyFilter = () => {
    const newFilters: { start_date?: string; end_date?: string } = {}
    if (startDate) newFilters.start_date = startDate
    if (endDate) newFilters.end_date = endDate
    setAppliedFilters(newFilters)

    // Clear selectedPeriod when applying custom dates
    setSelectedPeriod('')
  }

  const handleClearFilter = () => {
    setStartDate('')
    setEndDate('')
    setComparePeriods(5)
    setAppliedFilters({})
    setSelectedPeriod('') // Clear period selection when clearing filters
  }

  const handleQuickFilter = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
    const newFilters: { start_date?: string; end_date?: string } = {}
    if (start) newFilters.start_date = start
    if (end) newFilters.end_date = end
    setAppliedFilters(newFilters)
  }

  // Handlers for manual date changes that clear selected period
  const handleManualStartDateChange = (date: string) => {
    setStartDate(date)
    setSelectedPeriod('') // Clear period selection when manually changing dates
  }

  const handleManualEndDateChange = (date: string) => {
    setEndDate(date)
    setSelectedPeriod('') // Clear period selection when manually changing dates
  }

  // Helper function to calculate comparison period dates
  const getComparisonPeriodDates = (
    currentStart: string,
    currentEnd: string,
    periodIndex: number
  ) => {
    const start = new Date(currentStart)
    const end = new Date(currentEnd)
    const diffDays = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    )

    const periodStart = new Date(start)
    const periodEnd = new Date(end)

    // Format dates in local timezone to avoid UTC conversion issues
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    // For custom date ranges, shift by the inclusive day count to avoid overlap
    // diffDays gives us the time difference, but we need inclusive days for proper period length
    const inclusiveDays = diffDays + 1
    const shiftDays = inclusiveDays * periodIndex
    periodStart.setDate(periodStart.getDate() - shiftDays)
    periodEnd.setDate(periodEnd.getDate() - shiftDays)

    return {
      start: formatLocalDate(periodStart),
      end: formatLocalDate(periodEnd),
    }
  }

  // Build query parameters
  const queryParams = new URLSearchParams()
  if (appliedFilters.start_date)
    queryParams.append('start_date', appliedFilters.start_date)
  if (appliedFilters.end_date)
    queryParams.append('end_date', appliedFilters.end_date)
  const queryString = queryParams.toString()

  // Organisation stats derived from user data (using existing API)
  const [orgStepCounts, setOrgStepCounts] = useState<{ [key: string]: number }>(
    {}
  )
  const [totalOrgs, setTotalOrgs] = useState(0)
  const [orgApiResponse, setOrgApiResponse] = useState<any>(null)

  // Combined API call includes user count now

  // API call for new subscriptions data (for date analysis view)
  const {
    data: newSubscriptionsResponse,
    isLoading: isNewSubscriptionsLoading,
    isError: isNewSubscriptionsError,
  } = useCustom({
    url: `/users/new-subscriptions-data${
      queryString
        ? `?${queryString}&include_comparisons=${
            comparePeriods > 0
          }&comparison_periods=${comparePeriods}`
        : `?include_comparisons=${
            comparePeriods > 0
          }&comparison_periods=${comparePeriods}`
    }`,
    method: 'get',
    queryOptions: {
      enabled: analysisView === 'date', // Only fetch when in date analysis view
      onSuccess: (response: any) => {
        // New subscriptions data received successfully
      },
      onError: (error) => {
        // Error fetching new subscriptions data
      },
    },
    meta: {
      queryKey: [
        'new-subscriptions-data',
        appliedFilters,
        analysisView,
        comparePeriods,
      ],
    },
  })

  const newSubscriptionsData: NewSubscriptionData[] =
    newSubscriptionsResponse?.data?.data || []

  // Main API call for organisation stats with comparison periods included
  const { isLoading: isUserDataLoading, isError: isUserDataError } = useCustom({
    url: `/users/onboarding-org-stats${
      queryString
        ? `?${queryString}&include_comparisons=${
            comparePeriods > 0
          }&comparison_periods=${comparePeriods}`
        : `?include_comparisons=${
            comparePeriods > 0
          }&comparison_periods=${comparePeriods}`
    }`,
    method: 'get',
    queryOptions: {
      onSuccess: (response: any) => {
        // Process the organisation stats data from backend - Refine wraps response in 'data'
        const data = response.data || response
        const stepCounts: { [key: string]: number } = {}
        const stepTotalReached: { [key: string]: number } = {}
        data.step_breakdown?.forEach((item: any) => {
          stepCounts[item.step] = item.count
          if (item.total_reached !== undefined) {
            stepTotalReached[item.step] = item.total_reached
          }
        })

        setOrgStepCounts(stepCounts)
        setTotalOrgs(data.total_orgs || 0)
        setTotalUsers(data.total_users || 0) // Now get user count from this endpoint
        setOrgApiResponse(data)

        // Process comparison periods if available
        if (data.comparison_periods && data.comparison_periods.length > 0) {
          const comparisonData: Array<
            Array<{
              value: number
              cumulativeCount: number
              conversionRate: string
              atStep?: number
            }>
          > = []

          const comparisonUserCounts: number[] = []
          const comparisonUserBreakdowns: Array<{
            users_no_org: number
            users_created_org: number
            users_added_to_org: number
          }> = []

          data.comparison_periods.forEach((period: any) => {
            const processedData = processOrgStatsData(period)
            comparisonData.push(processedData)
            comparisonUserCounts.push(period.total_users || 0)
            if (period.user_breakdown) {
              comparisonUserBreakdowns.push(period.user_breakdown)
            }
          })

          setComparisonData(comparisonData)
          setComparisonUserCounts(comparisonUserCounts)
          setComparisonUserBreakdowns(comparisonUserBreakdowns)
        } else {
          // Clear comparison data if no periods returned
          setComparisonData([])
          setComparisonUserCounts([])
          setComparisonUserBreakdowns([])
        }
      },
      onError: () => {},
    },
    meta: {
      queryKey: [
        'onboarding-org-stats-combined',
        appliedFilters,
        comparePeriods,
      ],
    },
  })

  // User details API call removed - accordion sections deleted

  const isLoading = isUserDataLoading
  const isError = isUserDataError

  // Create data array from organisation step counts
  const data = useMemo(() => {
    // Get the special step data for steps that need extra info
    const convertedToPaidStep = orgApiResponse?.step_breakdown?.find(
      (item: any) => item.step === 'step_6_converted_to_paid'
    )

    const cancelledSubscription = orgApiResponse?.step_breakdown?.find(
      (item: any) => item.step === 'cancelled'
    )

    return [
      {
        name: 'Only Completed Step 1 - Setup Org',
        value: orgStepCounts['step_1_setup_org'] || 0,
      },
      {
        name: 'Only Completed Step 2 - Uploaded Docs',
        value: orgStepCounts['step_2_upload_docs'] || 0,
      },
      {
        name: 'Only Completed Step 3 - Org Profile',
        value: orgStepCounts['step_3_org_profile'] || 0,
      },
      {
        name: 'Only Completed Step 4 - Add Funding Request',
        value: orgStepCounts['step_4_funding_request'] || 0,
      },
      {
        name: 'Only Completed Step 5 - Scan for Matches',
        value: orgStepCounts['step_5_scan_matches'] || 0,
      },
      {
        name: 'Only Completed Step 6 - Converted to Paid',
        value: orgStepCounts['step_6_converted_to_paid'] || 0,
        _totalReached: convertedToPaidStep?.total_reached,
      },
      {
        name: 'Completed Onboarding - Drafted an Application',
        value: orgStepCounts['completed_onboarding'] || 0,
      },
      {
        name: 'Cancelled Membership',
        value: orgStepCounts['cancelled'] || 0,
        _totalReached: cancelledSubscription?.total_reached,
      },
    ]
  }, [orgStepCounts, orgApiResponse])

  // Comparison data is now handled in the main API call above
  const [comparisonUserCounts, setComparisonUserCounts] = useState<number[]>([])
  const [comparisonUserBreakdowns, setComparisonUserBreakdowns] = useState<
    Array<{
      users_no_org: number
      users_created_org: number
      users_added_to_org: number
    }>
  >([])

  // Comparison data is now properly managed by the API call

  // Helper function to process organisation stats API response
  const processOrgStatsData = (response: any) => {
    // Handle Refine response wrapper
    const data = response.data || response

    const stepCounts: number[] = []
    const stepTotalReached: (number | null)[] = []
    const stepAtStep: (number | null)[] = []
    const stepOrder = [
      'step_1_setup_org',
      'step_2_upload_docs',
      'step_3_org_profile',
      'step_4_funding_request',
      'step_5_scan_matches',
      'step_6_converted_to_paid',
      'completed_onboarding',
      'cancelled',
    ]

    stepOrder.forEach((stepKey) => {
      const stepData = data.step_breakdown?.find(
        (item: any) => item.step === stepKey
      )
      stepCounts.push(stepData ? stepData.count : 0)
      stepTotalReached.push(stepData?.total_reached || null)
      stepAtStep.push(stepData?.at_step || null)
    })

    const totalOrgs = data.total_orgs || 0

    return stepCounts.map((count, index) => {
      // Calculate cumulative count - orgs that reached this step or beyond
      let cumulativeCount

      if (
        stepOrder[index] === 'step_6_converted_to_paid' &&
        stepTotalReached[index] !== null
      ) {
        // Use the total_reached value from backend for converted to paid
        cumulativeCount = stepTotalReached[index]
      } else if (stepOrder[index] === 'cancelled') {
        cumulativeCount = stepTotalReached[index] || 0
      } else if (index === 0) {
        // First step (Setup Org) - all orgs reached this step
        cumulativeCount = totalOrgs
      } else {
        // For other steps, sum all steps from this one onwards
        cumulativeCount = stepCounts
          .slice(index)
          .reduce((sum, step) => sum + step, 0)
      }

      // Calculate conversion rate based on total org count
      const conversionRate =
        totalOrgs > 0
          ? ((cumulativeCount / totalOrgs) * 100).toFixed(1)
          : '0.0'

      return {
        value: count,
        cumulativeCount,
        conversionRate,
        atStep: stepAtStep[index] ?? undefined,
      }
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error loading dashboard data. Please try again.
        </Typography>
      </Box>
    )
  }

  const pieChartData = data.map((item) => ({
    name: item.name,
    value: item.value,
    percentage: ((item.value / totalOrgs) * 100).toFixed(2),
    _totalReached: item._totalReached,
    atStep: (item as any).atStep,
  }))

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Onboarding Stats
      </Typography>

      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleManualStartDateChange}
        onEndDateChange={handleManualEndDateChange}
        onApplyFilter={handleApplyFilter}
        onClearFilter={handleClearFilter}
        onQuickFilter={handleQuickFilter}
        comparePeriods={comparePeriods}
        onComparePeriodsChange={setComparePeriods}
        selectedPeriod={selectedPeriod}
        onPeriodChange={setSelectedPeriod}
        showComparePeriods={true}
      />

      {/* Analysis View Toggle */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Analysis Type
        </Typography>
        <ToggleButtonGroup
          value={analysisView}
          exclusive
          onChange={(event, value) => {
            if (value !== null) {
              setAnalysisView(value)
            }
          }}
          aria-label="analysis view"
          size="small"
        >
          <ToggleButton value="cohort" aria-label="cohort analysis">
            Cohort Analysis
          </ToggleButton>
          <ToggleButton value="date" aria-label="date analysis">
            Date Analysis
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {analysisView === 'cohort'
            ? 'Shows the onboarding funnel with conversion rates and step completion analysis'
            : 'Shows new subscriptions (organisations with first charges) over time periods'}
        </Typography>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          {analysisView === 'cohort' ? (
            <OnboardingStepSummary
              data={pieChartData}
              totalUsers={totalUsersFromOrgStats || totalUsers}
              totalOrgs={totalOrgs}
              comparePeriods={comparePeriods}
              startDate={startDate}
              endDate={endDate}
              comparisonData={comparisonData}
              comparisonUserCounts={comparisonUserCounts}
              selectedPeriod={selectedPeriod}
              onCountClick={handleCountClick}
              userBreakdown={orgApiResponse?.user_breakdown}
              comparisonUserBreakdowns={comparisonUserBreakdowns}
            />
          ) : (
            <>
              <MembershipLifecycle
                pauseResumeMetrics={orgApiResponse?.pause_resume_metrics}
                comparisonPauseResumeMetrics={
                  orgApiResponse?.comparison_periods?.map(
                    (period: any) => period?.pause_resume_metrics
                  ) || []
                }
                startDate={startDate}
                endDate={endDate}
                comparePeriods={comparePeriods}
                selectedPeriod={selectedPeriod}
                onLifecycleClick={handleLifecycleClick}
              />
              <Box sx={{ mt: 3 }}>
                <NewSubscriptionsChart
                  data={newSubscriptionsData}
                  isLoading={isNewSubscriptionsLoading}
                  isError={isNewSubscriptionsError}
                  startDate={startDate}
                  endDate={endDate}
                  orgsWithoutPayments={
                    newSubscriptionsResponse?.data?.orgs_without_payments || []
                  }
                />
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      <UpcomingRenewals />

      {/* Popup Dialog for Detailed View */}
      <Dialog
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {popupData.title}
          <IconButton onClick={() => setPopupOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {popupData.data.length > 0 ? (
            popupData.dataType === 'org' ? (
              <OrgDataTable
                data={popupData.data as OrgOnboardingData[]}
                csvTitle={popupData.csvTitle}
                adminUser={popupData.adminUser}
              />
            ) : popupData.dataType === 'users-without-orgs' ? (
              <UserDataTable
                data={popupData.data as any[]}
                csvTitle={popupData.csvTitle}
                adminUser={popupData.adminUser}
              />
            ) : (
              <OnboardingDataTable
                data={popupData.data as OnboardingUserData[]}
                csvTitle={popupData.csvTitle}
              />
            )
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {popupData.dataType === 'users-without-orgs'
                  ? 'These are users who have signed up but have not yet created an organisation.'
                  : popupData.dataType === 'org'
                  ? 'No organisations found for this step and date range.'
                  : 'No detailed user data available for this step.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {popupData.dataType === 'users-without-orgs'
                  ? 'Individual user details are not currently available in this view. The count represents users who have created accounts but have not yet set up their first organisation.'
                  : popupData.dataType === 'org'
                  ? 'Try adjusting your date range or checking if organisations exist for this criteria.'
                  : 'This count represents organisations at this step, but detailed user information is not currently available.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lifecycle Orgs Dialog (Paused/Resumed/Cancelled) */}
      <Dialog
        open={lifecycleDialogOpen}
        onClose={() => setLifecycleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {lifecycleData.title}
          <IconButton onClick={() => setLifecycleDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {lifecycleData.orgs.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Payment Type</TableCell>
                  {lifecycleData.type === 'paused' && (
                    <>
                      <TableCell>Paused On</TableCell>
                      <TableCell>Resumes On</TableCell>
                      <TableCell>Reason</TableCell>
                    </>
                  )}
                  {lifecycleData.type === 'resumed' && (
                    <>
                      <TableCell>Paused On</TableCell>
                      <TableCell>Resumed On</TableCell>
                    </>
                  )}
                  {lifecycleData.type === 'cancelled' && (
                    <TableCell>Cancelled On</TableCell>
                  )}
                  {lifecycleData.type === 'reactivated' && (
                    <TableCell>Reactivated On</TableCell>
                  )}
                  {lifecycleData.type === 'new_paid' && (
                    <TableCell>Converted On</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {lifecycleData.orgs.map((org) => (
                  <TableRow key={org.uuid}>
                    <TableCell>
                      <Typography
                        component="a"
                        href={`/orgs/${org.uuid}`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {org.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{org.payment_type || '-'}</TableCell>
                    {lifecycleData.type === 'paused' && (
                      <>
                        <TableCell>
                          {org.pause_start_date
                            ? new Date(org.pause_start_date).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {org.pause_end_date
                            ? new Date(org.pause_end_date).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>{org.pause_reason || '-'}</TableCell>
                      </>
                    )}
                    {lifecycleData.type === 'resumed' && (
                      <>
                        <TableCell>
                          {org.pause_start_date
                            ? new Date(org.pause_start_date).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {org.resumed_at
                            ? new Date(org.resumed_at).toLocaleDateString()
                            : '-'}
                        </TableCell>
                      </>
                    )}
                    {lifecycleData.type === 'cancelled' && (
                      <TableCell>
                        {org.cancelled_at
                          ? new Date(org.cancelled_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                    )}
                    {lifecycleData.type === 'reactivated' && (
                      <TableCell>
                        {org.reactivated_at
                          ? new Date(org.reactivated_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                    )}
                    {lifecycleData.type === 'new_paid' && (
                      <TableCell>
                        {org.converted_at
                          ? new Date(org.converted_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No organisations found for this category.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
